import React, { createContext, useContext, useState, useEffect } from 'react';
import { getStorageUsage, getCloudStorageInfo, isLocalStorageAvailable } from '../utils/storage';

/**
 * StorageContext
 * 
 * Manages storage information (local and cloud) for the entire app.
 * Provides real-time storage usage statistics.
 * 
 * Usage:
 * const { localStorageInfo, cloudStorageInfo, updateStorage } = useStorage();
 */

const StorageContext = createContext();

export const StorageProvider = ({ children }) => {
  const [localStorageInfo, setLocalStorageInfo] = useState(null);
  const [cloudStorageInfo, setCloudStorageInfo] = useState(null);
  const [isAvailable, setIsAvailable] = useState(true);

  // Initialize storage info on mount
  useEffect(() => {
    updateStorage();
  }, []);

  const updateStorage = () => {
    try {
      // Check if localStorage is available
      const available = isLocalStorageAvailable();
      setIsAvailable(available);

      if (available) {
        // Get local storage usage
        const localInfo = getStorageUsage();
        setLocalStorageInfo(localInfo);

        // Get cloud storage usage
        const cloudInfo = getCloudStorageInfo();
        setCloudStorageInfo(cloudInfo);

        console.log('Storage updated:', {
          local: localInfo,
          cloud: cloudInfo
        });
      }
    } catch (error) {
      console.error('Error updating storage info:', error);
      setIsAvailable(false);
    }
  };

  const value = {
    localStorageInfo,
    cloudStorageInfo,
    isAvailable,
    updateStorage,
    
    // Convenience getters
    get isLocalStorageFull() {
      return localStorageInfo && localStorageInfo.usedPercent > 90;
    },
    
    get canSaveToCloud() {
      return cloudStorageInfo && cloudStorageInfo.canSaveMore;
    },
    
    get cloudLimit() {
      return cloudStorageInfo?.displayLimit || cloudStorageInfo?.limit || 'Unknown';
    }
  };

  return (
    <StorageContext.Provider value={value}>
      {children}
    </StorageContext.Provider>
  );
};

export const useStorage = () => {
  const context = useContext(StorageContext);
  if (!context) {
    throw new Error('useStorage must be used within StorageProvider');
  }
  return context;
};

export default StorageContext;

